import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../Sidebar";
import "./LeadDetailsTable.css";
import API_BASE_URL from "../.././../config/env";

interface Lead {
  id: number;
  cust_name: string;
  cust_number: string;
  cust_email: string;
  lead_stage: number;
  is_valid: number;
  closed: number;
  created_at: string;
  project_name: string;
  city: string;
  zone: string;
  last_disposition_id: number | null;
  last_disposition: string | null;
  raw_lead_id?: number | null;
}

interface LeadDetailsTableProps {
  userId: string;
  type: string;
  city?: string;
  filterType?: string;
  fromDate?: string;
  toDate?: string;
}

const LeadDetailsTable: React.FC<LeadDetailsTableProps> = ({ 
  userId, 
  type, 
  city,
  filterType = 'overall',
  fromDate,
  toDate
}) => {
  const navigate = useNavigate();
  
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  const token = localStorage.getItem("authToken");
  const limit = 10;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch lead data
  useEffect(() => {
    if (userId && type) {
      fetchLeads();
    }
  }, [userId, type, city, page, debouncedSearch, filterType, fromDate, toDate]);

  const fetchLeads = async () => {
    if (!token) {
      setError("Authentication token not found. Please login again.");
      setLoading(false);
      return;
    }

    if (!userId) {
      setError("User ID is required");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let url = `${API_BASE_URL}/api/partner-count/leads/city-wise-lead-list/${userId}`;
      
      const params = new URLSearchParams();
      params.append('type', type);
      params.append('page', String(page));
      params.append('limit', String(limit));
      
      if (city) {
        params.append('city', city);
      }
      
      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }

      // Add filter type and date filters if available
      if (filterType) {
        params.append('filter_type', filterType);
      }
      if (fromDate) {
        params.append('from_date', fromDate);
      }
      if (toDate) {
        params.append('to_date', toDate);
      }
      
      const fullUrl = `${url}?${params.toString()}`;
      
      const response = await axios.get(
        fullUrl,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept": "application/json",
          },
          timeout: 30000,
        }
      );
      
      if (response.data.status === "success") {
        setLeads(response.data.items || []);
        setTotal(response.data.total || 0);
        setTotalPages(response.data.totalPages || 1);
      } else {
        setError(response.data.message || "Failed to fetch leads");
      }
    } catch (err: any) {
      console.error("Error fetching leads:", err);
      
      let errorMessage = "Failed to fetch leads";
      
      if (err.response) {
        if (err.response.status === 404) {
          errorMessage = "API endpoint not found. Please check the URL.";
        } else if (err.response.status === 401) {
          errorMessage = "Unauthorized. Please login again.";
        } else if (err.response.status === 403) {
          errorMessage = "You don't have permission to access this data.";
        } else if (err.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        } else {
          errorMessage = err.response.data?.message || err.response.data?.error || errorMessage;
        }
      } else if (err.request) {
        errorMessage = "No response from server. Please check your internet connection.";
      } else {
        errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Handle Lead ID click - Navigate to lead timeline
  const handleLeadIdClick = (leadId: number) => {
    navigate(`/lead/${leadId}`);
  };

  // Handle Lead Name click - Navigate to raw lead details (for NOT Q type)
  const handleLeadNameClick = (lead: Lead) => {
    // For NOT Q type, navigate to raw lead details
    if (type === 'not_q') {
      const rawLeadId = lead.raw_lead_id || lead.id;
      navigate(`/rawlead/${rawLeadId}`);
    } else {
      // For other types, navigate to lead timeline
      navigate(`/lead/${lead.id}`);
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const getStatusBadge = (lead: Lead) => {
    // For NOT Q type, show "Not Q" badge
    if (type === 'not_q') {
      return <span className="badge badge-not-q">Not Q</span>;
    }
    if (lead.is_valid === 0) {
      return <span className="badge badge-invalid">Invalid</span>;
    }
    if (lead.closed === 1) {
      return <span className="badge badge-closed">Closed</span>;
    }
    if (lead.lead_stage === 2) {
      return <span className="badge badge-scheduled">Scheduled</span>;
    }
    if (lead.lead_stage === 4) {
      return <span className="badge badge-f2f">F2F</span>;
    }
    return <span className="badge badge-active">Active</span>;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      'total': 'All Leads',
      'active': 'Active Leads',
      'invalid': 'Invalid Leads',
      'scheduled': 'Scheduled Leads',
      'site_visit': 'Site Visit',
      'f2f': 'F2F Leads',
      'token_done': 'Token Done',
      'transaction': 'Transactions',
      'not_q': 'Not Q Leads'
    };
    return labels[type] || type;
  };

  // Generate pagination numbers
  const getPaginationNumbers = () => {
    const numbers = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      numbers.push(i);
    }
    return numbers;
  };

  // If no userId, show message
  if (!userId) {
    return (
      <div className="lead-details-page">
        <Sidebar />
        <div className={`lead-details-content ${sidebarCollapsed ? 'expanded' : ''}`}>
          <div className="lead-details-container">
            <div className="lead-details-error">
              <span className="error-icon">⚠️</span>
              <h3>No User Selected</h3>
              <p>Please select a partner to view leads</p>
              <button onClick={goBack} className="back-btn">
                ← Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading && page === 1) {
    return (
      <div className="lead-details-page">
        <Sidebar />
        <div className={`lead-details-content ${sidebarCollapsed ? 'expanded' : ''}`}>
          <div className="lead-details-container">
            <div className="lead-details-loading">
              <div className="spinner"></div>
              <p>Loading leads...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lead-details-page">
        <Sidebar />
        <div className={`lead-details-content ${sidebarCollapsed ? 'expanded' : ''}`}>
          <div className="lead-details-container">
            <div className="lead-details-error">
              <span className="error-icon">⚠️</span>
              <h3>Error Loading Leads</h3>
              <p>{error}</p>
              <button onClick={fetchLeads} className="retry-btn">
                Try Again
              </button>
              <button onClick={goBack} className="back-btn">
                ← Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lead-details-page">
      <Sidebar />
      <div className={`lead-details-content ${sidebarCollapsed ? 'expanded' : ''}`}>
        <div className="lead-details-container">
          <div className="lead-details-table-wrapper">
            {/* Header Section */}
            <div className="table-header-section">
              <div className="table-title">
                <button onClick={goBack} className="back-btn-small">
                  ← Back
                </button>
                <h3>{getTypeLabel(type)} {city && `- ${city}`}</h3>
                <span className="total-count">Total: {total}</span>
              </div>
              <div className="table-search">
                <input
                  type="text"
                  placeholder="Search by name, number or project..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>

            {/* Table */}
            <div className="table-wrapper">
              <table className="lead-details-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Lead ID</th>
                    <th>Customer Name</th>
                    <th>Email</th>
                    <th>Project</th>
                    <th>City</th>
                    <th>Zone</th>
                    <th>Status</th>
                    <th>Last Disposition</th>
                    <th>Created Date</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.length > 0 ? (
                    leads.map((lead, index) => (
                      <tr key={lead.id}>
                        <td>{(page - 1) * limit + index + 1}</td>
                        <td>
                          <span 
                            className="clickable-lead-id"
                            onClick={() => handleLeadIdClick(lead.id)}
                          >
                            {lead.id || 'N/A'}
                          </span>
                        </td>
                        <td>
                          <span 
                            className={`clickable-lead-name ${type === 'not_q' ? 'not-q-link' : ''}`}
                            onClick={() => handleLeadNameClick(lead)}
                            title={type === 'not_q' ? 'Click to view raw lead details' : 'Click to view lead timeline'}
                          >
                            {lead.cust_name || 'N/A'}
                            {type === 'not_q' && <span className="link-icon">🔗</span>}
                          </span>
                        </td>
                        <td>{lead.cust_email || 'N/A'}</td>
                        <td>{lead.project_name || 'N/A'}</td>
                        <td>{lead.city || 'N/A'}</td>
                        <td>{lead.zone || 'N/A'}</td>
                        <td>{getStatusBadge(lead)}</td>
                        <td>{lead.last_disposition || 'N/A'}</td>
                        <td>{formatDate(lead.created_at)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="no-data">
                        No leads found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="table-pagination">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={page === 1}
                  className="pagination-btn pagination-first"
                >
                  ⟪
                </button>
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="pagination-btn pagination-prev"
                >
                  ‹ Previous
                </button>
                
                <div className="pagination-numbers">
                  {getPaginationNumbers().map((num) => (
                    <button
                      key={num}
                      onClick={() => handlePageChange(num)}
                      className={`pagination-number ${num === page ? 'active' : ''}`}
                    >
                      {num}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  className="pagination-btn pagination-next"
                >
                  Next ›
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={page === totalPages}
                  className="pagination-btn pagination-last"
                >
                  ⟫
                </button>
              </div>
            )}
            
            {/* Pagination Info */}
            <div className="pagination-info-bar">
              <span>Showing {(page - 1) * limit + 1} - {Math.min(page * limit, total)} of {total} leads</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetailsTable;